import _ from 'lodash';
import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';

// Data
import {
  buildCreatePlayerContact,
  buildRemovePlayerContact,
  buildUpdatePlayerContact
} from '../../../lib/services/player-contact.service';
import { CONTACT_FIELDS } from '../../../lib/queries/player-contact.queries';

// Components
import { ManageContactsContainer } from './manage-contacts.container';
import { IPlayerContactFormFields } from '../player-contact.types';
import { ContactRelationship, SportInvolvement } from '../../../lib/types';

const GET_MANAGE_PLAYER_CONTACTS = gql`
  query GetManagePlayerContacts($playerId: ID, $playerAssessmentId: ID) {
    playerContacts(playerId: $playerId, playerAssessmentId: $playerAssessmentId) {
      ...ContactFields
    }
  }

  ${CONTACT_FIELDS}
`;

interface IManagePlayerContactsGraphQL {
  playerId?: string;
  playerAssessmentId?: string;
}

export const ManagePlayerContactsGraphQL: React.FC<IManagePlayerContactsGraphQL> = ({ playerId, playerAssessmentId }) => {
  const { data, refetch } = useQuery(GET_MANAGE_PLAYER_CONTACTS, {
    skip: !playerId,
    variables: { playerId, playerAssessmentId },
    fetchPolicy: 'cache-and-network'
  });
  const { enqueueSnackbar } = useSnackbar();
  const { removePlayerContact } = buildRemovePlayerContact({playerAssessmentId});
  const { createPlayerContact } = buildCreatePlayerContact({playerAssessmentId});
  const { updatePlayerContact } = buildUpdatePlayerContact({playerAssessmentId});

  const contacts = _.get(data, 'playerContacts', []);

  const addContact = async (data: IPlayerContactFormFields) => {
    try {
      const response = await createPlayerContact({
        ...data,
        playerId,
        height: data.height || undefined,
        sportInvolvement: data.sportInvolvement?.value as SportInvolvement,
        relationship: data.relationship?.value as ContactRelationship
      });

      const result = response?.data?.createPlayerContact;

      if (result) {
        if (result.status === 'multiple_duplicates') {
          // Email matches one contact, phone matches another
          enqueueSnackbar('Contact info already exists across multiple contacts', { variant: 'warning' });
        } else if (result.wasUpdated) {
          // Contact was merged with an existing contact
          enqueueSnackbar('Contact updated - merged with existing contact', { variant: 'success' });
          // Refetch to get the latest data
          await refetch();
        } else {
          // New contact was created
          enqueueSnackbar('Contact created successfully', { variant: 'success' });
        }
      }

      return response;
    } catch (error) {
      enqueueSnackbar('Failed to save contact', { variant: 'error' });
      throw error;
    }
  };


  return <ManageContactsContainer
    contacts={contacts}
    addContact={addContact}
    removeContact={removePlayerContact}
    updateContact={updatePlayerContact}
  />;
};
